import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, FileDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { API } from "@/config";
import { ItemsEditor, emptyItem } from "./ItemsEditor";

const STATUSES = ["draft", "sent", "paid"];
const INTRA = (state) => ["uttar pradesh", "up", "09"].includes((state || "").trim().toLowerCase());

export const InvoicesSection = () => {
  const [invoices, setInvoices] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [customer, setCustomer] = useState({ name: "", address: "", state: "Uttar Pradesh", gstin: "" });
  const [items, setItems] = useState([emptyItem()]);

  const fetchInvoices = async () => {
    const { data } = await axios.get(`${API}/invoices`, { withCredentials: true });
    setInvoices(data);
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const totals = useMemo(() => {
    let subtotal = 0, tax = 0;
    items.forEach((it) => {
      const amt = (it.qty || 0) * (it.rate || 0);
      subtotal += amt;
      tax += (amt * (it.gst_rate || 0)) / 100;
    });
    const intra = INTRA(customer.state);
    return {
      subtotal, cgst: intra ? tax / 2 : 0, sgst: intra ? tax / 2 : 0,
      igst: intra ? 0 : tax, total: subtotal + tax,
    };
  }, [items, customer.state]);

  const createInvoice = async (e) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(
        `${API}/invoices`,
        { customer, items: items.filter((it) => it.description.trim()), status: "sent" },
        { withCredentials: true }
      );
      toast.success(`Invoice ${data.invoice_no} created`);
      setShowAdd(false);
      setCustomer({ name: "", address: "", state: "Uttar Pradesh", gstin: "" });
      setItems([emptyItem()]);
      fetchInvoices();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    }
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/invoices/${id}`, { status }, { withCredentials: true });
    toast.success("Status updated");
    fetchInvoices();
  };

  const deleteInvoice = async (id) => {
    await axios.delete(`${API}/invoices/${id}`, { withCredentials: true });
    toast.success("Invoice deleted");
    fetchInvoices();
  };

  return (
    <div data-testid="invoices-section">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{invoices.length} invoices</p>
        <Button size="sm" className="gap-1" data-testid="add-invoice-button" onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4" /> New Invoice
        </Button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
        <Table data-testid="invoices-table">
          <TableHeader>
            <TableRow>
              <TableHead>Invoice No</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Total</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoices.map((inv) => (
              <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="font-medium">{inv.invoice_no}</TableCell>
                <TableCell>{inv.date}</TableCell>
                <TableCell>{inv.customer.name}</TableCell>
                <TableCell className="font-semibold">₹{inv.total}</TableCell>
                <TableCell>
                  <Select value={inv.status} onValueChange={(v) => setStatus(inv.id, v)}>
                    <SelectTrigger className="w-28" data-testid={`invoice-status-${inv.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1"
                      data-testid={`invoice-pdf-${inv.id}`}
                      onClick={() => window.open(`${API}/invoices/${inv.id}/pdf`, "_blank")}
                    >
                      <FileDown className="h-3.5 w-3.5" /> PDF
                    </Button>
                    <Button variant="ghost" size="icon" data-testid={`invoice-delete-${inv.id}`} onClick={() => deleteInvoice(inv.id)} aria-label="Delete invoice">
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-invoice-dialog" className="max-h-[90vh] max-w-3xl overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">New GST Invoice</DialogTitle>
          </DialogHeader>
          <form onSubmit={createInvoice} className="grid gap-4" data-testid="add-invoice-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Customer Name *</Label>
                <Input required value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} data-testid="invoice-customer-name" />
              </div>
              <div className="grid gap-2">
                <Label>Customer GSTIN</Label>
                <Input value={customer.gstin} onChange={(e) => setCustomer({ ...customer, gstin: e.target.value })} data-testid="invoice-customer-gstin" />
              </div>
              <div className="grid gap-2">
                <Label>Address</Label>
                <Input value={customer.address} onChange={(e) => setCustomer({ ...customer, address: e.target.value })} data-testid="invoice-customer-address" />
              </div>
              <div className="grid gap-2">
                <Label>State *</Label>
                <Input required value={customer.state} onChange={(e) => setCustomer({ ...customer, state: e.target.value })} data-testid="invoice-customer-state" />
              </div>
            </div>
            <div className="grid gap-2">
              <Label>Items *</Label>
              <ItemsEditor items={items} setItems={setItems} />
            </div>
            <div className="rounded-xl bg-muted/50 p-4 text-sm" data-testid="invoice-totals-preview">
              <div className="flex justify-between"><span>Subtotal</span><span>₹{totals.subtotal.toFixed(2)}</span></div>
              {totals.igst > 0 ? (
                <div className="flex justify-between"><span>IGST</span><span>₹{totals.igst.toFixed(2)}</span></div>
              ) : (
                <>
                  <div className="flex justify-between"><span>CGST</span><span>₹{totals.cgst.toFixed(2)}</span></div>
                  <div className="flex justify-between"><span>SGST</span><span>₹{totals.sgst.toFixed(2)}</span></div>
                </>
              )}
              <div className="mt-1 flex justify-between border-t border-border pt-1 font-bold">
                <span>Grand Total</span><span>₹{totals.total.toFixed(2)}</span>
              </div>
            </div>
            <Button type="submit" data-testid="invoice-submit-button">Create Invoice</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
